// A segmented fast toggle (a row of aria-pressed buttons, radio-like) —
// shared primitive behind AccountMenu's in-menu appearance/language rows
// (FE-010, D14) and the public-page LocaleSwitcher (issue 443). Extracted so
// both consumers share one a11y-reviewed implementation instead of drifting
// copies.
//
// Not role="menu"/role="radiogroup": a plain button group with aria-pressed
// (role="group" + a labelled panel) is simpler and keeps the host axe-clean
// while still being fully keyboard operable (each button is a normal Tab
// stop).

export interface SegmentedToggleOptions<T extends string> {
  // CSS hook for the group; each button gets `${baseClass}-btn`. Left to the
  // caller so one instance can be a compact in-menu chip (AccountMenu) and
  // another a 44px public-page touch target (LocaleSwitcher) without a
  // shared, over-general class trying to serve both.
  baseClass: string;
  ariaLabel: string;
  values: readonly T[];
  labelFor: (value: T) => string;
  current: T;
  onSelect: (value: T) => void;
  // `live` updates aria-pressed on the buttons in place — for a control that
  // re-renders itself without a remount (theme). Pass false when the
  // caller's own onSelect already tears down and rebuilds this node (a
  // locale switch remounts the route), so there is nothing left to update.
  live: boolean;
}

export function segmentedToggle<T extends string>(opts: SegmentedToggleOptions<T>): HTMLElement {
  const group = document.createElement('span');
  group.className = opts.baseClass;
  group.setAttribute('role', 'group');
  group.setAttribute('aria-label', opts.ariaLabel);
  const buttons: HTMLButtonElement[] = [];
  for (const value of opts.values) {
    const btn = document.createElement('button');
    btn.className = `${opts.baseClass}-btn`;
    btn.type = 'button';
    btn.textContent = opts.labelFor(value);
    btn.dataset.value = value;
    btn.setAttribute('aria-pressed', String(value === opts.current));
    btn.addEventListener('click', () => {
      if (opts.live) for (const b of buttons) b.setAttribute('aria-pressed', String(b === btn));
      opts.onSelect(value);
    });
    buttons.push(btn);
    group.appendChild(btn);
  }
  return group;
}
